import { filterSpecificPlaces } from "./place-term-classification";
import { extractDestinationFromPrompt } from "./destination-extraction";
import { extractDestinationCandidate } from "./trip-request-readiness";
import { extractSignatureSpecialtyVenues } from "./local-specialties";

export type GeographicScope = "city" | "regional" | "named_route" | "multi_city";
export type RequestedStopCategory =
  | "cafe" | "restaurant" | "hotel" | "viewpoint" | "museum" | "monument"
  | "church" | "park" | "nature" | "water" | "shopping" | "nightlife" | "market";

export type DeterministicTripIntent = {
  namedRoute?: string;
  requiredPlaces: string[];
  geographicScope?: GeographicScope;
  startPlace?: string;
  startFromUserLocation?: boolean;
};

export type FlexiblePlaceRequest = {
  requestedStopQuery?: string;
  categoryFocusQuery?: string;
};

const clean = (value: string) => value
  .replace(/^[\s,;:.\-–—]+|[\s,;:.\-–—]+$/g, "")
  .replace(/\s+/g, " ")
  .trim();

const DURATION_NUMBER_WORDS: Record<string, number> = {
  um: 1, uma: 1, one: 1,
  dois: 2, duas: 2, two: 2,
  tres: 3, three: 3,
  quatro: 4, four: 4,
  cinco: 5, five: 5,
  seis: 6, six: 6,
  sete: 7, seven: 7,
  oito: 8, eight: 8,
  nove: 9, nine: 9,
  dez: 10, ten: 10,
  onze: 11, eleven: 11,
  doze: 12, twelve: 12,
};

const parseDurationValue = (value: string): number | undefined => {
  const numeric = Number.parseFloat(value.replace(",", "."));
  if (Number.isFinite(numeric)) return numeric;
  const normalized = value.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  return DURATION_NUMBER_WORDS[normalized];
};

const stripRouteQualifiers = (value: string) => clean(value)
  .replace(/\s+\b(?:hoje|amanhã|depois\s+de\s+amanhã|este\s+fim\s+de\s+semana|no\s+fim\s+de\s+semana|na\s+próxima\s+semana|para\s+a\s+semana|durante\s+\d+\s+(?:horas?|dias?)|em\s+\d+\s+(?:horas?|dias?)|de\s+carro|a\s+pé|de\s+bicicleta|de\s+mota|de\s+transportes?\s+públicos?|com\s+a\s+minha\s+família|com\s+crianças?)\b.*$/i, "")
  .replace(/\s+\b(?:today|tomorrow|this\s+weekend|next\s+week|for\s+\d+\s+(?:hours?|days?)|in\s+\d+\s+(?:hours?|days?)|by\s+car|on\s+foot|by\s+bike|by\s+motorbike|by\s+public\s+transport)\b.*$/i, "")
  .trim();

const dedupe = (values: string[]) => {
  const seen = new Set<string>();
  return values.filter((value) => {
    const key = value.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const PLACE_QUERY_ALIASES: Array<{ re: RegExp; query: string }> = [
  { re: /\b(?:gelatarias?|sorveterias?|ice[ -]?cream\s+shops?|glaciers?|eisdielen?)\b/i, query: "ice cream shop" },
  { re: /\b(?:pastelarias?|confeitarias?|padarias?|baker(?:y|ies)|patisseries?|panader[ií]as?|boulangeries?)\b/i, query: "bakery" },
  { re: /\b(?:caf[eé]s?|coffee\s+shops?|cafeter[ií]as?)\b/i, query: "cafe" },
  { re: /\b(?:bibliotecas?|libraries?|biblioth[eè]ques?|bibliotheken?)\b/i, query: "library" },
  { re: /\b(?:livrarias?|bookstores?|book\s+shops?|librairies?|buchhandlungen?)\b/i, query: "bookstore" },
  { re: /\b(?:igrejas?|iglesias?|churches?|[eé]glises?|kirchen?)\b/i, query: "church" },
  { re: /\b(?:mesquitas?|mosques?|mosqu[eé]es?)\b/i, query: "mosque" },
  { re: /\b(?:sinagogas?|synagogues?)\b/i, query: "synagogue" },
  { re: /\b(?:templos?|temples?)\b/i, query: "temple" },
  { re: /\b(?:museus?|museums?|mus[eé]es?)\b/i, query: "museum" },
  { re: /\b(?:mercados?|markets?|march[eé]s?)\b/i, query: "market" },
  { re: /\b(?:parques?|jardins?|parks?|gardens?)\b/i, query: "park" },
  { re: /\b(?:farm[aá]cias?|pharmacies?|apotheken?)\b/i, query: "pharmacy" },
];

const cleanFlexiblePlaceQuery = (value: string): string | undefined => {
  const normalizedValue = value.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const alias = PLACE_QUERY_ALIASES.find(({ re }) => re.test(normalizedValue));
  if (alias) return alias.query;

  const cleaned = clean(value)
    .replace(/^(?:um|uma|uns|umas|o|a|os|as|an?|the|un|une|des|el|la|los|las)\s+/i, "")
    .replace(/\b(?:bom|boa|bons|boas|bem\s+avaliad[oa]s?|t[ií]pic[oa]s?|local|locais|good|best|well[- ]rated|typical|mejor(?:es)?|bien\s+valorad[oa]s?)\b/gi, "")
    .replace(/\s+(?:perto|pr[oó]xim[oa]|junto|na\s+rota|no\s+percurso|near|close\s+to|along\s+the\s+route|en\s+la\s+ruta|sur\s+le\s+trajet)\b.*$/i, "")
    .replace(/\s+(?:em|in|en|à|at)\s+[\p{L}\p{N}'’ -]{2,60}$/iu, "")
    .replace(/\s+/g, " ")
    .trim();
  return cleaned.length >= 2 ? cleaned.slice(0, 100) : undefined;
};

export function detectFlexiblePlaceRequest(
  prompt: string,
  requiredPlaces: string[] = extractRequiredPlaces(prompt),
): FlexiblePlaceRequest {
  const focusPatterns = [
    /\b(?:roteiro|rota|itiner[aá]rio|tour|route|itinerary|recorrido)\s+(?:s[oó]|apenas|somente|only|just)?\s*(?:(?:de|d[oa]s?|por|of|for|von)\s+|d['’]\s*)([^,.!?;\n]{2,100})/i,
    /\b(?:quero|pretendo|gostava\s+de|want)\s+(?:um|uma|an?|the)?\s*(?:roteiro|rota|itiner[aá]rio|tour|route|itinerary)\s+(?:de|d[oa]s?|por|of|for)\s+([^,.!?;\n]{2,100})/i,
    /(?:^|[.!?;\n])\s*(?:(?:i\s+want|quero|pretendo|gostava\s+de)\s+(?:an?|um|uma)?\s*)?([^,.!?;\n]{2,100}?)\s+(?:tour|route|itinerary|roteiro|rota|itiner[aá]rio|recorrido)\b/i,
  ];
  for (const [index, pattern] of focusPatterns.entries()) {
    const value = prompt.match(pattern)?.[1];
    if (
      index === 2 &&
      value &&
      /\b(?:recomenda|recomendar|sugere|sugerir|inclui|incluir|adiciona|adicionar|mete|meter|recommend|suggest|include|add|recomienda|incluye|añade|ajoute)\b/i.test(value)
    ) {
      continue;
    }
    const query = value ? cleanFlexiblePlaceQuery(value) : undefined;
    if (query) return { categoryFocusQuery: query };
  }

  const hasClearlyNamedPlace = requiredPlaces.some((place) =>
    !/^(?:um|uma|uns|umas|an?|some)\b/i.test(place.trim()),
  );
  if (hasClearlyNamedPlace) return {};

  const requestedStopPatterns = [
    /\b(?:recomenda|recomendar|sugere|sugerir|inclui|incluir|adiciona|adicionar|mete|meter)\s+(?:me\s+)?([^,.!?;\n]{2,100})/i,
    /\b(?:recommend|suggest|include|add)\s+(?:me\s+)?([^,.!?;\n]{2,100})/i,
    /\b(?:recomienda|sugiere|incluye|añade|agrega)\s+([^,.!?;\n]{2,100})/i,
    /\b(?:recommande|sugg[eè]re|ajoute|inclus)\s+([^,.!?;\n]{2,100})/i,
  ];
  for (const pattern of requestedStopPatterns) {
    const value = prompt.match(pattern)?.[1];
    const query = value ? cleanFlexiblePlaceQuery(value) : undefined;
    if (query) return { requestedStopQuery: query };
  }
  return {};
}

export function detectNamedRoute(prompt: string): string | undefined {
  const patterns = [
    /\b(?:fazer|seguir|percorrer|conhecer|visitar|explorar)\s+(?:a|o)\s+((?:rota|caminho|estrada|trilho|circuito|percurso)\s+(?:d[oa]s?\s+)?[^,.!?;\n]{2,90})/i,
    /\b((?:rota|caminho|estrada|trilho|circuito|percurso)\s+(?:d[oa]s?\s+)?[^,.!?;\n]{2,90})\b/i,
    /\b((?:route|trail|road|way|circuit)\s+(?:of\s+)?[^,.!?;\n]{2,90})\b/i,
  ];
  for (const pattern of patterns) {
    const match = prompt.match(pattern)?.[1];
    const value = match ? stripRouteQualifiers(match) : "";
    const genericReference = /^(?:rota|caminho|estrada|trilho|circuito|percurso|route|trail|road|way|circuit)\s+(?:por\s+)?(?:lá|la|aí|ai|aqui|dali|there|here)$/i.test(value);
    if (value.length >= 4 && !genericReference) return value;
  }
  return undefined;
}

export function extractRequiredPlaces(prompt: string): string[] {
  const signatures = extractSignatureSpecialtyVenues(prompt);
  const captures: string[] = signatures.map((entry) => entry.venue);
  const patterns = [
    /(?:quero|pretendo|gostava\s+de|tenho\s+de|preciso\s+de)\s+(?:visitar|passar\s+por|incluir|conhecer)\s+(?:exatamente\s+|obrigatoriamente\s+)?([^.!?\n]+)/gi,
    /(?:quero|pretendo|gostava\s+de|tenho\s+de|preciso\s+de)\s+passar\s+(?:pela|pelo|na|no)\s+([^.!?\n]+)/gi,
    /(?:quero|pretendo|gostava\s+de|tenho\s+de|preciso\s+de)\s+ir\s+(?:à|a|ao|na|no)\s+((?:loja|pastelaria|confeitaria|padaria|bakery|shopping|centro\s+comercial|mercado|café|cafe|restaurante|museu|igreja|parque|livraria)\b[^.!?\n]*)/gi,
    /\b((?:loja|pastelaria|confeitaria|padaria|bakery|shopping|mercado|café|cafe|restaurante|museu|igreja|parque|livraria)\s+(?:d[oa]s?|de|del|du|of|the)\s+[\p{L}\p{N}'’&-]+(?:\s+[\p{L}\p{N}'’&-]+){0,3})/giu,
    /(?:podes?|poderias?|consegues?|garante(?:s)?\s+que)\s+(?:também\s+)?(?:passar|passes?|ir)\s+(?:pela|pelo|à|ao|na|no)\s+([^.!?\n]+)/gi,
    /(?:podes?|poderias?|consegues?)\s+(?:também\s+)?(?:incluir|adicionar|meter)\s+(?:a|o|as|os)?\s*([^.!?\n]+)/gi,
    /(?:inclui|incluir|adiciona|adicionar|não\s+te\s+esqueças\s+de)\s+(?:obrigatoriamente\s+)?([^.!?\n]+)/gi,
    /(?:must\s+visit|include|visit\s+exactly)\s+([^.!?\n]+)/gi,
  ];

  for (const pattern of patterns) {
    for (const match of prompt.matchAll(pattern)) {
      if (match[1]) captures.push(match[1]);
    }
  }

  const stopWords = /\b(?:em|durante|por|com|sem|de\s+carro|a\s+pé|de\s+bicicleta|de\s+transportes|hoje|amanhã|este\s+fim\s+de\s+semana)\b.*$/i;
  const signatureNames = new Set(signatures.map((entry) => entry.venue.toLocaleLowerCase()));
  const places = captures.flatMap((capture) => {
    if (signatureNames.has(capture.toLocaleLowerCase())) return [capture];
    const shortened = capture.replace(stopWords, "");
    return shortened
      .replace(/\s+(?:e\s+depois|depois|e\s+terminar|terminando|acabando)\b.*$/i, "")
      .split(/\s*(?:,|;)\s*|\s+(?:e|and|y)\s+/i)
      .map(clean)
      .filter((value) => value.length >= 3 && value.length <= 120)
      .filter((value) => !/^(?:uma?|o|a|os|as|alguns?|pontos?|locais?|sítios?|lugares?)$/i.test(value))
      .filter((value) => !/^(?:um|uma|algum|alguma)\s+(?:(?:bom|boa|bem\s+avaliad[oa]|local|típico|típica)\s+)*(?:café|cafe|pastelaria|confeitaria|padaria|bakery|gelataria|sorveteria|ice\s+cream\s+shop|biblioteca|library|livraria|bookstore|igreja|church|mesquita|mosque|sinagoga|synagogue|templo|temple|restaurante|loja|mercado|shopping)(?:\s+(?:bom|boa|bem\s+avaliad[oa]|local|típico|típica|perto|próxim[oa]|na\s+rota|no\s+percurso)\b.*)?$/i.test(value));
  });

  return filterSpecificPlaces(dedupe(places)).slice(0, 12);
}

export function detectRequestedStopCategory(
  prompt: string,
  requiredPlaces: string[] = extractRequiredPlaces(prompt),
): RequestedStopCategory | undefined {
  if (requiredPlaces.length) return undefined;

  const p = prompt.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const asksForOne =
    /\b(?:recomenda|recomendar|sugere|sugerir|inclui|incluir|adiciona|adicionar|mete|meter)\b/.test(p) ||
    /\b(?:quero|gostava\s+de|pretendo)\s+(?:parar|passar|ir)\s+(?:numa?|a\s+uma|por\s+uma)\b/.test(p) ||
    /\b(?:recommend|suggest|include|add)\b/.test(p);
  if (!asksForOne) return undefined;

  const categories: Array<{ id: RequestedStopCategory; re: RegExp }> = [
    { id: "cafe", re: /\b(?:cafes?|coffee\s*shops?|pastelarias?|confeitarias?|padarias?|baker(?:y|ies))\b/ },
    { id: "restaurant", re: /\b(?:restaurantes?|restaurants?|tascas?|bistros?)\b/ },
    { id: "shopping", re: /\b(?:lojas?|shops?|shopping|centros?\s+comerciais|malls?)\b/ },
    { id: "market", re: /\b(?:mercados?|markets?|feiras?)\b/ },
    { id: "viewpoint", re: /\b(?:miradouros?|viewpoints?)\b/ },
    { id: "museum", re: /\b(?:museus?|museums?)\b/ },
    { id: "park", re: /\b(?:parques?|jardins?|parks?|gardens?)\b/ },
  ];
  return categories.find(({ re }) => re.test(p))?.id;
}

const cleanStartPlace = (value: string) => clean(value)
  .replace(/\s+\b(?:e|and|y)\s+(?=(?:(?:a|o|um|uma|the)\s+)?(?:confeitaria|pastelaria|padaria|bakery|café|cafe|restaurante|loja|mercado|shopping|museu|igreja|parque|livraria)\b).*$/i, "")
  .replace(/\s+\b(?:para|e)\s+(?:fazer|começar|iniciar|criar|seguir)\s+(?:a|o|um|uma)?\s*(?:rota|roteiro|itinerário|visita|route|itinerary|trip)\b.*$/i, "")
  .replace(/\s+\b(?:durante|por|em|for)\s+\d+\s+(?:horas?|dias?|hours?|days?)\b.*$/i, "")
  .replace(/\s+\b(?:hoje|amanhã|este\s+fim\s+de\s+semana|today|tomorrow|this\s+weekend)\b.*$/i, "")
  .trim()
  .slice(0, 120);

export function extractStartPlace(prompt: string): string | undefined {
  const patterns = [
    /\b(?:vou|irei|vamos|ia)\s+(?:de|em)\s+(?:comboio|autocarro|metro|avi[aã]o|carro)\s+(?:a|ao|à|para|at[eé])\s+([^,.!?;\n]{2,120}?)\s*[,;:]?\s*(?:e\s+)?(?:podes?|quero|pretendo|para)\s+(?:começar|iniciar|criar)\b[^.!?;\n]*?(?:por\s+l[aá]|a[ií]|nesse\s+local|dali)/i,
    /\b(?:a\s+partir\s+d[aoe]s?|desde)\s+([^,.!?;\n]{2,120})/i,
    /\b(?:começar|comece|começo|iniciar|inicie|in[ií]cio|partir)\s+(?:(?:o|a)\s+(?:roteiro|rota|visita|itinerário)\s+)?(?:a\s+partir\s+d[aoe]s?|em|no|na|do|da)\s+([^,.!?;\n]{2,120})/i,
    /\b(?:vou|ia|irei|iremos|vamos)\s+chegar\s+(?:a|ao|à|em|no|na)\s+([^,.!?;\n]{2,120})/i,
    /\bcheg(?:o|arei|aremos)\s+(?:a|ao|à|em|no|na)\s+([^,.!?;\n]{2,120})/i,
    /\b(?:starting\s+from|start\s+(?:the\s+route\s+)?(?:from|at)|begin\s+(?:from|at)|arrive\s+at)\s+([^,.!?;\n]{2,120})/i,
  ];
  for (const pattern of patterns) {
    const match = prompt.match(pattern)?.[1];
    const value = match ? cleanStartPlace(match) : "";
    if (value.length >= 3) return value;
  }

  // A future arrival by plane plus an explicit statement that the traveller
  // will be at/near the airport is a named origin, not a request for current
  // GPS. Keep the label generic here; geocoding combines it with destination
  // city and country so this works internationally.
  const normalized = prompt.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const mentionsAirTravel = /\b(?:aviao|voo|plane|flight|avion|flugzeug)\b/.test(normalized);
  const saysNearAirport =
    /\b(?:a\s+beira|perto|proximo|junto)\s+d[oa]\s+aeroporto\b/.test(normalized) ||
    /\b(?:near|close\s+to|by)\s+the\s+airport\b/.test(normalized) ||
    /\b(?:cerca|junto)\s+del\s+aeropuerto\b/.test(normalized) ||
    /\bpres\s+de\s+l\s*aeroport\b/.test(normalized) ||
    /\b(?:am|nahe\s+dem)\s+flughafen\b/.test(normalized);
  if (mentionsAirTravel && saysNearAirport) {
    if (/\baeropuerto\b/.test(normalized)) return "Aeropuerto";
    if (/\baeroport\b/.test(normalized)) return "Aéroport";
    if (/\bflughafen\b/.test(normalized)) return "Flughafen";
    if (/\bairport\b/.test(normalized)) return "Airport";
    return "Aeroporto";
  }

  return undefined;
}

export function detectStartFromUserLocation(prompt: string, startPlace?: string): boolean | undefined {
  if (startPlace) return false;

  const p = prompt.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const futurePlanning = /\b(?:amanha|depois\s+de\s+amanha|na\s+proxima\s+semana|quando\s+chegar|vou\s+chegar|ia\s+chegar|irei\s+chegar|chegarei|vou\s+estar|irei\s+estar|i\s+will\s+arrive|when\s+i\s+arrive|tomorrow|next\s+week)\b/.test(p);
  if (futurePlanning) return false;

  const explicitCurrentLocation =
    /\b(?:estou|estamos|encontro-me|encontramo-nos|ja\s+cheguei|acabei\s+de\s+chegar)\s+(?:em|no|na|perto\s+de|junto\s+de)\b/.test(p) ||
    /\b(?:a\s+partir\s+de\s+onde\s+estou|daqui|usar\s+(?:a\s+)?minha\s+localizacao|minha\s+localizacao|where\s+i\s+am|my\s+(?:current\s+)?location|i\s+am\s+(?:in|at|near))\b/.test(p);
  if (explicitCurrentLocation) return true;

  const explicitFutureTrip =
    /\b(?:vou|vamos|irei|iremos|ia)\s+(?:a|ao|a\s+|para)\s+[^,.!?;\n]+/.test(p) ||
    /\b(?:i\s+am\s+going\s+to|i\s+will\s+visit|planning\s+to\s+visit)\b/.test(p);
  if (explicitFutureTrip) return false;

  return undefined;
}

export type BasicTripRequest = {
  city?: string;
  durationHours?: number;
  startTime?: string;
  mode?: "walk" | "scooter" | "bike" | "car" | "transit";
};

export function isArrivalTransportOnly(prompt: string, startPlace = extractStartPlace(prompt)): boolean {
  if (!startPlace) return false;
  const p = prompt.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  return /\b(?:vou|irei|vamos|ia)\s+(?:de|em)\s+(?:comboio|autocarro|metro|aviao|train|bus|subway|plane)\s+(?:a|ao|para|ate|to)\b/.test(p);
}

export function hasExplicitStartTime(prompt: string): boolean {
  const normalized = prompt.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  if (/\b\d{1,2}:\d{2}\b/.test(normalized)) return true;
  if (/\b\d{1,2}\s*h(?:\s*\d{1,2})?\b/.test(normalized)) return true;
  return /\b(?:as|perto\s+das?|cerca\s+das?|por\s+volta\s+das?|at|around|about|a\s+las|vers|gegen|um)\s*\d{1,2}(?::\d{2})?\s*(?:horas?|h|am|pm)?\b/.test(normalized);
}

export function extractBasicTripRequest(prompt: string): BasicTripRequest {
  // Destino: extração local robusta (artigos/preposições, cidades compostas,
  // origem vs. destino, PT/EN/ES/FR/IT/DE) antes de qualquer IA ou Maps.
  const city = extractDestinationFromPrompt(prompt).city ?? extractDestinationCandidate(prompt);

  const durationMatches = Array.from(
    prompt.matchAll(/\b(\d+(?:[.,]\d+)?|um(?:a)?|dois|duas|tr[eê]s|quatro|cinco|seis|sete|oito|nove|dez|onze|doze|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve)\s*(horas?|hours?|dias?|days?)\b/gi),
  );
  const durationMatch = durationMatches.find((match) => {
    const index = match.index ?? 0;
    const before = prompt.slice(Math.max(0, index - 32), index);
    return !/(?:perto|cerca|por\s+volta)\s+d(?:as?|os?)?\s*$|(?:às|as)\s*$/i.test(before);
  });
  let durationHours: number | undefined;
  if (durationMatch) {
    const value = parseDurationValue(durationMatch[1]);
    const unit = durationMatch[2].toLowerCase();
    const hours = typeof value === "number" ? (/dia|day/.test(unit) ? value * 24 : value) : undefined;
    if (typeof hours === "number" && Number.isFinite(hours) && hours >= 0.5 && hours <= 336) {
      durationHours = hours;
    }
  }

  const startTimeMatch =
    prompt.match(/\b(?:perto|cerca|por\s+volta)\s+d(?:as?|os?)?\s*(\d{1,2})(?:(?:[:h])\s*(\d{1,2}))?\s*(?:horas?)?\b/i) ??
    prompt.match(/\b(?:chego|chegar|chegarei|vou\s+chegar|irei\s+chegar|começo|comecar|começar|inicio|iniciar)\s+(?:perto\s+d(?:as?)?\s+|(?:às|as)\s+)(\d{1,2})(?:(?:[:h])\s*(\d{1,2}))?\s*(?:horas?)?\b/i);
  let startTime: string | undefined;
  if (startTimeMatch) {
    const hour = Number.parseInt(startTimeMatch[1], 10);
    const minute = Number.parseInt(startTimeMatch[2] || "0", 10);
    if (hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59) {
      startTime = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
    }
  }

  const p = prompt.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const arrivalTransportOnly = isArrivalTransportOnly(prompt);
  const explicitSightseeingTransit =
    /\b(?:roteiro|rota|visita|visitar|conhecer|explorar|deslocar(?:-me)?|andar)\b[^.!?\n]{0,60}\b(?:transportes?\s+publicos?|metro|autocarro|bus|subway|public\s+transport)\b/.test(p);
  const mode =
    /\b(comboio|autocarro|metro|transportes?\s+publicos?|train|bus|subway|public\s+transport)\b/.test(p) && (!arrivalTransportOnly || explicitSightseeingTransit) ? "transit" :
    /\b(carro|automovel|car|driving)\b/.test(p) ? "car" :
    /\b(bicicleta|bike|bicycle)\b/.test(p) ? "bike" :
    /\b(trotinete|scooter)\b/.test(p) ? "scooter" :
    /\b(a\s+pe|walking|on\s+foot)\b/.test(p) ? "walk" :
    undefined;

  return { city, durationHours, startTime, mode };
}

export function detectGeographicScope(prompt: string, namedRoute?: string): GeographicScope | undefined {
  const p = prompt.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  if (namedRoute) return "named_route";
  const explicitlyMultipleCities =
    /\b(varias|várias|multiplas|múltiplas)\s+cidades\b|\bentre\s+[^,.]+\s+e\s+[^,.]+/i.test(prompt);
  // Detect proper-name city lists without maintaining a country-specific list.
  // Capitalisation keeps category lists such as "museus, igrejas" from being
  // mistaken for multiple destinations.
  const namedDestinationList =
    /\b(?:visitar|conhecer|explorar|percorrer|passar\s+por|visit|explore|tour)\s+(?:[oa]\s+)?[\p{Lu}][\p{L}'’ -]{1,60}\s*[,;]\s*(?:[oa]\s+)?[\p{Lu}][\p{L}'’ -]{1,60}(?:\s+(?:e|and|y|et|und)\s+(?:[oa]\s+)?[\p{Lu}][\p{L}'’ -]{1,60})?/u.test(prompt);
  if (explicitlyMultipleCities || namedDestinationList) {
    return "multi_city";
  }
  if (/\b(regiao|regional|arredores|douro|vale|serra|costa|litoral|interior|vinhos?|aldeias?|varios municipios|vários municípios|road trip|estrada panoramica|estrada panorâmica)\b/.test(p)) {
    return "regional";
  }
  return undefined;
}

export function extractDeterministicTripIntent(prompt: string): DeterministicTripIntent {
  const namedRoute = detectNamedRoute(prompt);
  const startPlace = extractStartPlace(prompt);
  return {
    namedRoute,
    requiredPlaces: extractRequiredPlaces(prompt),
    geographicScope: detectGeographicScope(prompt, namedRoute),
    startPlace,
    startFromUserLocation: detectStartFromUserLocation(prompt, startPlace),
  };
}
